import { PageHeader } from '@metorial/layout';
import { useBoot } from '@metorial/state';
import { Avatar, Button, Text, theme } from '@metorial/ui';
import { RiExternalLinkLine } from '@remixicon/react';
import { useMemo } from 'react';
import { styled } from 'styled-components';
import { EmptyText } from '../../organization/pages/_kit';

// Account › Portals (cloud-spec §8): the portals this person can open.

let Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
`;

let Card = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid ${theme.colors.gray400};
  border-radius: 10px;
  background: ${theme.colors.background};
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    border-color: ${theme.colors.gray600};
    background: ${theme.colors.gray100};
  }
`;

let Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;

`;

let Url = styled.span`
  overflow-wrap: anywhere;
  font-size: 12px;
  color: ${theme.colors.gray600};
`;

export let AccountPortalsPage = () => {
  let boot = useBoot();

  let portals = useMemo(() => {
    let seen = new Set<string>();
    let out: {
      id: string;
      name: string;
      imageUrl: string | null;
      url: string;
      organization: string;
    }[] = [];

    for (let consumer of boot.data?.consumers ?? []) {
      for (let profile of consumer.profiles ?? []) {
        let portal = (profile as any).portal;
        if (!portal || portal.status != 'active' || seen.has(portal.id)) continue;
        seen.add(portal.id);
        let url = portal.urls?.find((u: any) => u.type == 'namespace')?.url ?? portal.urls?.[0]?.url;
        if (!url) continue;
        out.push({
          id: portal.id,
          name: portal.name,
          imageUrl: portal.imageUrl ?? null,
          url,
          organization: profile.organization?.name ?? ''
        });
      }
    }

    return out;
  }, [boot.data]);

  return (
    <>
      <PageHeader title="Portals" description="Open the portals you can access." />

      {boot.isLoading && !boot.data ? (
        <EmptyText>Loading…</EmptyText>
      ) : portals.length == 0 ? (
        <EmptyText>You don't have access to any portals yet.</EmptyText>
      ) : (
        <Grid>
          {portals.map(portal => (
            <Card key={portal.id} href={portal.url} target="_blank" rel="noreferrer">
              <Avatar entity={{ name: portal.name, imageUrl: portal.imageUrl }} size={36} withInitials />
              <Body>
                <Text size="2" weight="strong">
                  {portal.name}
                </Text>
                <Url title={portal.url}>{portal.url.replace(/^https?:\/\//, '')}</Url>
                {portal.organization && (
                  <Text size="1" color="gray600">
                    {portal.organization}
                  </Text>
                )}
              </Body>
              <Button size="1" variant="outline" as="span" iconLeft={<RiExternalLinkLine />}>
                Open
              </Button>
            </Card>
          ))}
        </Grid>
      )}
    </>
  );
};
